import { useState } from 'react';
import { Loader2, Download, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const KNOWN_PROPERTIES = [
  { id: '249304', name: 'Marais Suite' },
];

export function BackfillPropertyPanel() {
  const [propertyId, setPropertyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runBackfill = async (id) => {
    const targetId = (id || propertyId).trim();
    if (!targetId) {
      toast.error('Enter a property ID');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.backfillProperty(targetId);
      setResult(data);
      toast.success(`Backfill complete: ${data.bookings_upserted ?? 0} bookings upserted`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Backfill Property Bookings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Triggers a full re-sync of all bookings for a single property from the Uplisting API.
          Use this after fixing response parsing or when a property has zero reservations.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Property ID (uplisting_id)
            </label>
            <input
              type="text"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              placeholder="e.g. 249304"
              className="h-9 rounded-md border border-input bg-background px-3 text-sm w-48"
            />
          </div>
          <Button onClick={() => runBackfill()} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Backfill
          </Button>
        </div>

        {/* Quick-action buttons for known properties */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Quick:</span>
          {KNOWN_PROPERTIES.map((prop) => (
            <Button
              key={prop.id}
              variant="outline"
              size="sm"
              className="text-xs h-7"
              disabled={loading}
              onClick={() => {
                setPropertyId(prop.id);
                runBackfill(prop.id);
              }}
            >
              {prop.name} ({prop.id})
            </Button>
          ))}
        </div>

        {/* Result */}
        {result && (
          <div className="rounded-lg border p-3 bg-muted/30 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Backfill complete</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Property ID</p>
                <p className="text-sm font-medium font-mono">{result.property_id}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bookings fetched</p>
                <p className="text-sm font-medium tabular-nums">{result.bookings_fetched ?? '-'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Upserted</p>
                <p className="text-sm font-medium tabular-nums">{result.bookings_upserted ?? '-'}</p>
              </div>
              {result.had_error != null && (
                <div>
                  <p className="text-xs text-muted-foreground">API Error</p>
                  <Badge variant={result.had_error ? 'destructive' : 'outline'} className="text-[10px]">
                    {result.had_error ? 'Yes' : 'No'}
                  </Badge>
                </div>
              )}
            </div>
            {result.error && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{result.error}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
